<template>
	<div ref="wrapperElement" :class="{'bg-white': isFullScreen}">
		<div v-if="isFullScreen" class="flex justify-between w-full pt-16 px-16">
			<div class="flex items-center">
				<div v-if="event.club && event.club.avatar_thumbnail" class="mr-16">
					<avatar :src="event.club.avatar_thumbnail" tile :size="64"/>
				</div>
				<div class="flex flex-column">
					<p class="text-4xl font-heading font-bold leading-normal text-text-color mb-4">{{ event.display_name }}</p>
					<p class="text-3xl font-heading font-bold leading-normal text-text-color mb-0">{{ selectedResultList ? selectedResultList.name : listGroup.name }}</p>
				</div>
			</div>
			<div class="flex ml-16">
				<cdn-image :src="Images.logo.path" alt="SPOFERAN" :size="{height: 64, ratio: Images.logo.ratio}" class="mr-32"/>
				<btn @click="isFullScreen = false" :title="$t('action.exit_fullscreen')">
					<Icon :icon="icons.minimize_screen"/>
				</btn>
			</div>
		</div>

		<data-table :headers="headers"
					:url="`/v1/events/${event.id}/result-lists/${listGroup.id}/ranks`"
					:color="event.color"
					:filter="filter"
					:params="params"
					:items-per-page="itemsPerPage"
					:hide-search="isFullScreen"
					:hide-pagination-controls="isFullScreen"
					simple-paginated
					full-width
					expand-on-mobile
					dense
					on-scroll
					:as-guest="props.asGuest"
					:break-header="false"
					:persist-filter-keys="['list', 'search']"
					:group-prop="selectedResultList ? null : 'result_list_id'"
					:groups="tableGroups"
					ref="tableComponent"
					show-inputs-on-mobile
					:no-data-text="$t('missing.results.index')"
					:no-results-text="$t('missing.search')"
					:sub-rows-prop="listGroup.rank_group_display === 'single' ? null : 'participations'"
					:sub-rows-existence-prop="listGroup.rank_group_display === 'single' ? null : 'has_multiple_participations'"
					:sub-rows-count-prop="listGroup.rank_group_display === 'single' ? null : 'participations_count'"
					@expand-row="handleToggledRankExpand">

			<template v-if="!isFullScreen && ($breakpoint.smAndUp || $slots.buttons)" #buttons>
				<slot name="buttons"/>

				<btn v-if="$breakpoint.smAndUp" @click="isFullScreen = true" :title="$t('action.open_in_fullscreen')" icon>
					<Icon :icon="icons.maximize_screen"/>
				</btn>
			</template>

			<template v-if="selectedResultList && selectedResultList.description" #header>
				<p class="text-subtext" :class="isFullScreen ? 'text-lg' : 'text-sm'">{{ selectedResultList.description }}</p>
			</template>

			<template v-if="!isFullScreen" v-slot:inputs>
				<form-select
					v-if="resultListOptions.length > 1"
					v-model="selectedResultListKey"
					:label="$t('label.result_list')"
					:unselect="$t('action.show_all')"
					:options="resultListOptions"
					:color="event.color"
					hide-details
					filterable
					inverted/>
			</template>

			<template v-slot:item.starter_number="{ item }">
				<chip v-if="item.starter_number" color="gray-300" small>{{ item.starter_number }}</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.primary_rank_value="{ item, parent }">
				<chip v-if="!parent && item.status && ![ParticipationStatus.Active, ParticipationStatus.Ranked, ParticipationStatus.TempRanked].includes(item.status.label)"
					  x-small>{{ item.status.title }}
				</chip>
				<div v-else class="flex items-center">
					<span class="whitespace-no-wrap">{{ parseRankMetricValue(listGroup.primary_rank_value_prop, item.primary_rank_value) }}</span>
					<tooltip v-if="item.status && item.status.label === ParticipationStatus.Active" right :text="item.status.description">
						<Icon :icon="icons.participation_active" :color="item.status.color" x-small class="ml-4"/>
					</tooltip>
					<tooltip v-else-if="item.status && item.status.label === ParticipationStatus.TempRanked" right :text="item.status.description">
						<Icon :icon="icons.participation_temp_ranked" :color="item.status.color" x-small class="ml-4"/>
					</tooltip>
				</div>
			</template>

			<template v-slot:item.secondary_rank_value="{ item }">
				<span class="table-text-line">{{ parseRankMetricValue(listGroup.secondary_rank_value_prop, item.secondary_rank_value) }}</span>
			</template>

			<template v-slot:item.avatar="{ item, parent, reduced }">
				<avatar :src="item.avatar_thumbnail" :size="24" :round="!!(!listGroup || listGroup.primary_rank_group_prop?.value !== 'club_name' || parent)"/>
			</template>

			<template v-slot:item.user="{ item, parent, reduced }">
				<div class="flex items-center">
					<avatar v-if="item.avatar_thumbnail !== undefined && (!reduced || headers.length < 5)" :src="item.avatar_thumbnail" :size="24" class="mr-8"
							:round="!!(!listGroup || listGroup.primary_rank_group_prop?.value !== 'club_name' || parent)"/>

					<div class="flex items-center">
						<nuxt-link v-if="!reduced && (parent || item.participation_id)"
								   :to="$web('events.participations.show', [event._key, !parent ? item.participation_id : item._key])" class="leading-none"
								   :style="{color: event.color}">
							<i v-if="item.private && !item.display_name" class="table-text-line">{{ $t('label.anonymous') }}</i>
							<span v-else class="table-text-line">{{ item.display_name }}</span>
						</nuxt-link>
						<template v-else>
							<i v-if="item.private && !item.display_name" class="table-text-line leading-tight">{{ $t('label.anonymous') }}</i>
							<span v-else class="table-text-line leading-tight">{{ item.display_name }}</span>
						</template>
						<tooltip v-if="item.private && item.display_name" bottom :text="$t('label.anonymous')">
							<Icon xx-small color="gray-300" class="ml-5" :icon="icons.anonymous"/>
						</tooltip>
					</div>
				</div>
			</template>

			<template v-slot:item.details="{ item, parent }">
				<btn v-if="parent || item.participation_id" :to="$web('events.participations.show', [event._key, !parent ? item.participation_id : item._key])" small
					 class="w-full h-30">
					<span>{{ $t('label.details') }}</span>
					<Icon :icon="icons.next"/>
				</btn>
			</template>

			<template v-slot:item.name="{ item, parent, reduced }">
				<div class="flex items-center">
					<nuxt-link v-if="!reduced && (parent || item.participation_id)"
							   :to="$web('events.participations.show', [event._key, !parent ? item.participation_id : item._key])" class="leading-none"
							   :style="{color: event.color}">
						<i v-if="item.private && !item.first_name" class="table-text-line">{{ $t('label.anonymous') }}</i>
						<span v-else class="table-text-line">{{ item.last_name }}{{ item.last_name && item.first_name ? ', ' : '' }}{{ item.first_name }}</span>
					</nuxt-link>
					<template v-else>
						<i v-if="item.private && !item.first_name" class="table-text-line leading-none">{{ $t('label.anonymous') }}</i>
						<span v-else class="table-text-line leading-none">{{ item.last_name }}{{ item.last_name && item.first_name ? ', ' : '' }}{{ item.first_name }}</span>
					</template>
					<tooltip v-if="item.private && item.first_name" bottom :text="$t('label.anonymous')">
						<Icon xx-small color="gray-300" class="ml-5" :icon="icons.anonymous"/>
					</tooltip>
				</div>
			</template>

			<template v-slot:item.certificate="{ item, parent }">
				<lazy-form-btn v-if="parent || item.participation_id" method="GET"
						  with-error
						  small
						  :action="`/v1/events/${event.id}/participations/${!parent ? item.participation_id : item.id}/certificates/download`"
						  @success="url => handleDownloadRequest('participation_certificate', url)">
					<span>{{ $t('action.download') }}</span>
				</lazy-form-btn>
			</template>

			<template v-slot:item.gender="{ item, parent, reduced }">
				<template v-if="item.gender === 'm'">{{ reduced ? $t('abbreviation.male') : $t('label.male') }}</template>
				<template v-else-if="item.gender === 'f'">{{ reduced ? $t('abbreviation.female') : $t('label.female') }}</template>
				<template v-else-if="listGroup && listGroup.primary_rank_group_prop && !parent">{{ reduced ? $t('abbreviation.mixed') : $t('label.mixed') }}</template>
				<template v-else>-</template>
			</template>

			<template v-slot:item.country="{ item }">
				<div v-if="item.country" class="flex items-center">
					<avatar :src="item.country.flag" :size="18" class="mr-8"/>
					<span class="table-text-line">{{ item.country.locale_name }}</span>
				</div>
				<span v-else>-</span>
			</template>

			<template v-slot:item.nationality="{ item, reduced }">
				<div v-if="item.nationality" class="flex items-center">
					<avatar :src="item.nationality.flag" :size="18"/>
					<span v-if="!reduced" class="table-text-line ml-8">{{ item.nationality.code }}</span>
				</div>
				<span v-else>-</span>
			</template>

			<template v-slot:item.private="{ item }">
				<span v-if="item.private === true" class="text-warning table-text-line">{{ $t('label.anonymous') }}</span>
				<span v-else-if="item.private === false" class="text-success table-text-line">{{ $t('label.public') }}</span>
				<span v-else>-</span>
			</template>

			<template v-slot:item.club="{ item, reduced }">
				<div class="flex items-center" :class="{'w-full justify-center': reduced}">
					<template v-if="item.club && item.club.name">
						<avatar :src="item.club.avatar_thumbnail" :size="18" tile/>
						<span v-if="!reduced" class="table-text-line ml-8">{{ truncate(item.club.name, 60) }}</span>
					</template>
					<span v-else>-</span>
				</div>
			</template>

			<template v-slot:item.age_class="{ item }">
				<span class="table-text-line">{{ item.ageClass ? (item.ageClass.abbreviation ? item.ageClass.abbreviation : item.ageClass.name) : '-' }}</span>
			</template>

			<template v-slot:item.team="{ item }">
				<span v-if="item.team" class="table-text-line">{{ item.team.name }}</span>
				<span v-else>-</span>
			</template>

			<template v-slot:item.status="{ item }">
				<chip v-if="item.status" :color="item.status.color" small :help="item.status.description">{{ item.status.title }}</chip>
			</template>

			<template v-if="props.listGroup.ranks_display_limit" v-slot:group_footer="{ group }">
				<tr>
					<td :colspan="headers.length">
						<btn @click="selectedResultListKey = group._key" :color="event.color" class="w-full" text small>{{ $t('action.show_all') }}</btn>
					</td>
				</tr>
			</template>

			<template #footer>
				<alert v-if="((selectedResultList && !selectedResultList.is_confirmed) || (!selectedResultList && !listGroup.is_confirmed)) && !isFullScreen" type="warning" small
					   :icon="icons.info" :content="$t('text.temporary_results')" text/>
			</template>

		</data-table>
	</div>
</template>

<script setup lang="ts">
import {mdiClose, mdiExclamation, mdiIncognito, mdiMonitorScreenshot, mdiRunFast} from '@mdi/js'
import type {EventPreview, ResultListGroup} from "@spoferan/spoferan-ts-core";
import {Images, ListGroupDisplay, parseRankMetricValue, ParticipationStatus, RankMetric, scrollToSelector, truncate} from "@spoferan/spoferan-ts-core";
import {infoIcon, nextIcon, refreshIcon} from "../../config";
import {FullScreenComponentEmits, type FullScreenComponentProps, useFullScreenComponent} from "../../composables/useFullScreenComponent";
import {useI18n, useNuxtApp} from "#imports";
import type {Ref} from "vue";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useParticipationColumnDataDisplays} from "../../composables/useParticipationColumnDataDisplays";

const {$date, $echo, $apiFetch, $analytics, $downloadFile} = useNuxtApp();
const {t} = useI18n();

const props = withDefaults(defineProps<FullScreenComponentProps & {

	// The event to show the ranks for
	event: EventPreview,

	// The result list group of the list to show the ranks for
	listGroup: ResultListGroup,

	// The key of the pre-selected result list
	list?: string,

	// Whether protected columns shall be shown
	withProtected?: boolean,

	asGuest?: boolean,

	cacheBuster?: number
}>(), {});

const emit = defineEmits([...FullScreenComponentEmits, 'list-selected']);

const icons = {
	maximize_screen: mdiMonitorScreenshot,
	minimize_screen: mdiClose,
	participation_active: mdiRunFast,
	participation_temp_ranked: mdiExclamation,
	anonymous: mdiIncognito,
	next: nextIcon,
	info: infoIcon,
	refresh: refreshIcon
};

const wrapperElement: Ref<HTMLElement|null> = ref(null);
const tableComponent = ref(null);

const itemsPerPage = ref(25);
const selectedResultListKey = defineModel('list', {
	type: String,
	default: null
});

const participationClasses = computed(() => {
	return props.listGroup.participationClasses && props.listGroup.participationClasses.length ? props.listGroup.participationClasses : [];
});

const paceType = computed(() => {

	// Only display the pace when it makes sense for the rank value props
	const paceMetrics = [RankMetric.Time, RankMetric.Distance, RankMetric.Rounds];
	if ((!props.listGroup.primary_rank_value_prop || !paceMetrics.includes(props.listGroup.primary_rank_value_prop)) && (!props.listGroup.secondary_rank_value_prop || !paceMetrics.includes(props.listGroup.secondary_rank_value_prop))) {
		return null;
	}

	const types = participationClasses.value.map(participationClass => participationClass && participationClass.settings && participationClass.settings.pace_type).filter((value, index, self) => value && self.indexOf(value) === index);

	return types.length === 1 ? types[0] : null
});

const resultListOptions = computed(() => {
	const options = [];
	if (props.listGroup && props.listGroup.lists && props.listGroup.lists.length) {
		props.listGroup.lists.forEach(list => {
			options.push({
				text: list.name,
				value: list._key,
				name: list.name,
				description: list.description,
				is_confirmed: list.is_confirmed,
				id: list.id,
				_key: list._key,
				group: list.track_spot_id ? t('label.intermediate_rank_values') : null
			})
		})
	}

	return options;
});

const selectedResultList = computed(() => {
	if (!resultListOptions.value.length) {
		return null;
	}
	if (resultListOptions.value.length === 1) {
		return resultListOptions.value[0];
	}

	return resultListOptions.value.find(option => option.value === selectedResultListKey.value);
});

const {isFullScreen} = useFullScreenComponent(wrapperElement, props, emit);
watch(isFullScreen, (newValue) => {
	itemsPerPage.value = newValue ? 100 : 10;
});

const {parseTableHeaderDataForColumn} = useParticipationColumnDataDisplays({
	resultListGroup: props.listGroup,
	paceType: paceType,
});

const params = {
	ts: props.cacheBuster
};

const headers = computed(() => {
	const headers = props.listGroup.columns.filter(column => props.withProtected || !column.is_protected).map(column => {
		return parseTableHeaderDataForColumn(column);
	});
	headers.push({
		text: ' ', // For proper alignment on description list
		value: 'details',
		sortable: false,
		width: 95
	})

	return headers;
});

const filter = computed(() => {
	const filter: {[key: string]: any} = {};

	if (selectedResultListKey.value) {
		filter.list = selectedResultListKey.value;
	}

	return filter;
});

const tableGroups = computed(() => {
	if (selectedResultList.value) {
		return null;
	}

	return props.listGroup.lists && props.listGroup.lists.length ? props.listGroup.lists.map(list => {
		return {id: list.id, title: list.name, description: list.description, _key: list._key}
	}) : null
});

watch(() => props.listGroup, () => {
	selectedResultListKey.value = null
});

watch(selectedResultListKey, () => {
	scrollToSelector(wrapperElement.value);
});

watch(selectedResultList, (newValue, oldValue) => {
	disableLiveUpdates(oldValue);
	activateLiveUpdates(newValue);
	emit('list-selected', newValue)
});

onMounted(() => {
	activateLiveUpdates(selectedResultList.value);
});

onBeforeUnmount(() => {
	disableLiveUpdates(selectedResultList.value);
})

function handleToggledRankExpand(rank, expanded) {
	if (expanded && props.listGroup.rank_group_display !== ListGroupDisplay.Single && rank.has_multiple_participations && !rank.participations) {
		rank['_expand_loading'] = true;
		$apiFetch(`/v1/events/${props.event.id}/result-lists/${props.listGroup.id}/ranks/${rank.id}/participations`, {
			guest: props.asGuest,
			params: {
				list: selectedResultListKey.value
			}
		}).then(response => {
			rank['participations'] = response.data;
		}).finally(() => {
			delete rank['_expand_loading'];
		})
	}
}

function activateLiveUpdates(list) {
	// We always enable live updates for testing, or when the event is over
	if (!props.event.is_test && ((!props.event.start_date || $date().isBefore(props.event.start_date)))) {
		return;
	}

	if (list) {
		console.log('Listening for updates on list: ' + list.name);
		$echo.subscribe('result_list.' + list.id)
			.listen('ranks.updated', () => {
				console.log('Ranks updated event received.');
				refreshTableForNewData();
			});
	} else {
		console.log('Listening for updates on list group: ' + props.listGroup.name);
		$echo.subscribe('result_list_group.' + props.listGroup.id)
			.listen('ranks.updated', () => {
				console.log('Ranks updated event received.');
				refreshTableForNewData();
			});
	}
}

function disableLiveUpdates(list) {
	if (!props.event.is_test && ((!props.event.start_date || $date().isBefore(props.event.start_date)))) {
		return;
	}

	if (list) {
		console.log('Stop listening for updates on list: ' + list.name);
		$echo.unsubscribe('result_list.' + list.id);
	} else {
		console.log('Stop listening for updates on list group: ' + props.listGroup.name);
		$echo.unsubscribe('result_list_group.' + props.listGroup.id);
	}
}

function handleDownloadRequest(type, url, name = undefined, key = undefined) {
	$analytics.track({
		event: 'download',
		model_type_name: type,
		model_name: name,
		model_key: key,
		url: url
	});

	$downloadFile(url);
}

function refreshTableForNewData() {
	// Only reload the last currently displayed page as it's most likely the one that has changed during live events
	if (tableComponent.value) {
		// Must call reload function like this
		// See https://github.com/vuejs/core/issues/5540
		tableComponent.value.$.exposed.reload(undefined, false, false);
	}
}

</script>